import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useGeminiAI } from '../hooks/useGeminiAI';
import { useAuth } from '../context/AuthContext';
import { useClientInsights } from '../hooks/useClientInsights';
import { AIMetadata, PRContext, Client } from '../types';

interface AIContextValue {
  generateResponse: (prompt: string, options?: {
    messageContext?: string[];
    conversationId?: string;
  }) => Promise<string>;
  isGenerating: boolean;
  provideFeedback: (messageId: string, feedback: 'positive' | 'negative', details?: string) => Promise<boolean>;
  setPRContext: (context: PRContext) => void;
  prContext: PRContext | null;
  setActiveClient: (client: Client | null) => void;
  activeClient: Client | null;
  addClientInsight: (insight: {
    type: 'key_message' | 'tone' | 'preference' | 'language' | 'audience' | 'writing_style' | 'press_release_analysis' | 'other',
    content: string,
    source: 'conversation' | 'manual' | 'press_release_analysis',
    sourceId?: string,
    sourceUrl?: string,
    confidence: number,
    verified: boolean
  }) => Promise<string | null>;
}

export const AIContext = createContext<AIContextValue | null>(null);

export const useAI = () => {
  const context = useContext(AIContext);
  if (!context) {
    throw new Error('useAI must be used within an AIProvider');
  }
  return context;
};

interface AIProviderProps {
  children: ReactNode;
}

export const AIProvider: React.FC<AIProviderProps> = ({ children }) => {
  const [prContext, setPRContext] = useState<PRContext | null>(null);
  const [activeClient, setActiveClient] = useState<Client | null>(null);
  const { generateResponse: geminiGenerate, provideFeedback, isGenerating } = useGeminiAI();
  const { currentUser } = useAuth(); // Corrected: use currentUser
  const { addInsight, fetchInsights } = useClientInsights();
  
  // Enhanced generate function that includes PR context, client data, and learned insights
  const generateResponse = async (prompt: string, options: {
    messageContext?: string[];
    conversationId?: string;
  } = {}) => {
    const { messageContext = [], conversationId } = options;
    
    let clientInsights: any[] = [];
    
    // If we have an active client, fetch their insights to include in the context
    if (activeClient?.id) {
      clientInsights = await fetchInsights(activeClient.id);
    }
    
    // Format insights to be included in the AI context
    const formattedInsights = clientInsights
      .filter(insight => insight.verified || insight.confidence > 0.7)
      .map(insight => `${insight.type.toUpperCase()}: ${insight.content} (${insight.verified ? 'verified' : `confidence: ${insight.confidence}`})`);
    
    // Separate press release specific insights for better context
    const pressReleaseInsights = clientInsights
      .filter(insight => ['tone', 'writing_style', 'press_release_analysis', 'key_message'].includes(insight.type) && 
                        (insight.verified || insight.confidence > 0.75))
      .map(insight => `${insight.type.toUpperCase()}: ${insight.content}`);
    
    // Build additional context
    let additionalContext = '';
    if (formattedInsights.length > 0) {
      additionalContext = `LEARNED CLIENT INSIGHTS:\n${formattedInsights.join('\n')}`;
    }
    
    // Add specific press release context if the prompt seems to be about press releases
    const isPressReleaseRequest = prompt.toLowerCase().includes('press release') || 
                                  prompt.toLowerCase().includes('pr announcement') ||
                                  prompt.toLowerCase().includes('media release');
    
    if (isPressReleaseRequest && pressReleaseInsights.length > 0) {
      additionalContext += `\n\nPRESS RELEASE STYLE GUIDELINES:\n${pressReleaseInsights.join('\n')}`;
      additionalContext += `\nIMPORTANT: Follow the learned tone, writing style, and structural patterns from previous press releases when creating new content.`;
    }
    
    // Use the active client if available, plus any additional PR context and learned insights
    return geminiGenerate(prompt, {
      messageContext,
      client: activeClient,
      conversationId,
      additionalContext: additionalContext || undefined
    });
  };
  
  // Function to add a new client insight
  const addClientInsight = async (insightData: {
    type: 'key_message' | 'tone' | 'preference' | 'language' | 'audience' | 'writing_style' | 'press_release_analysis' | 'other',
    content: string,
    source: 'conversation' | 'manual' | 'press_release_analysis',
    sourceId?: string,
    sourceUrl?: string,
    confidence: number,
    verified: boolean
  }) => {
    if (!activeClient?.id || !currentUser?.uid) return null; // Added check for currentUser.uid
    
    // Create the full insight data with clientId and createdBy included
    const fullInsightData = {
      ...insightData,
      clientId: activeClient.id,
      createdBy: currentUser.uid // Added createdBy
    };
    
    return await addInsight(activeClient.id, fullInsightData);
  };

  const value: AIContextValue = {
    generateResponse,
    isGenerating,
    provideFeedback,
    setPRContext,
    prContext,
    setActiveClient,
    activeClient,
    addClientInsight
  };

  return <AIContext.Provider value={value}>{children}</AIContext.Provider>;
};
